import React, { useState, useEffect } from 'react';
import { Icons } from '../constants';
import { BOARD_TEMPLATES, BoardTemplate } from '../templates';
import { AppTheme, UserProfile } from '../types';
import { supabase } from '../services/supabase';

interface OnboardingProps {
    onComplete: (userProfile: UserProfile, template?: BoardTemplate) => void;
    currentTheme: AppTheme;
    onThemeChange: (theme: AppTheme) => void;
    onBack?: () => void;
}

const Onboarding = ({ onComplete, currentTheme, onThemeChange, onBack }: OnboardingProps) => {
    const [step, setStep] = useState<'email' | 'otp' | 'profile' | 'setup'>('email');
    const [email, setEmail] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [name, setName] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState<BoardTemplate | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [, setIsNewUser] = useState(false);


    // Focus helpers
    const otpInputRef = React.useRef<HTMLInputElement>(null);
    const nameInputRef = React.useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (step === 'otp') otpInputRef.current?.focus();
        if (step === 'profile') nameInputRef.current?.focus();
    }, [step]);

    const handleSendOtp = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!email.trim()) return;
        setIsLoading(true);
        setError(null);

        try {
            const { error } = await supabase.auth.signInWithOtp({
                email,
                options: { shouldCreateUser: true }
            });
            if (error) throw error;
            setStep('otp');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOtp = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (otpCode.length !== 6) return;
        setIsLoading(true);
        setError(null);

        try {
            const { data, error } = await supabase.auth.verifyOtp({
                email,
                token: otpCode,
                type: 'email'
            });
            if (error) throw error;

            // Check if user has a profile
            const { data: profile } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', data.user?.id)
                .single();

            if (profile) {
                // Existing user - App.tsx will pick this up via onAuthStateChange
                // allowing natural transition, or we can prompt setup if desired.
                // For now, let's just let App.tsx handle the redirect if profile exists.
                // But if they clicked "Guest" before, we might need to be careful? 
                // Actually App.tsx handles login state.

                // If profile exists, we're done.
                // If not, we go to profile creation.
                setIsNewUser(false);
            } else {
                setIsNewUser(true);
                setStep('profile');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Invalid code');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateProfile = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!name.trim()) return;

        // We don't save to DB here; we pass to onComplete which handles it in App.tsx
        // OR we save it now to ensure it exists.
        // Let's go to Setup step first.
        setStep('setup');
    };

    const handleComplete = () => {
        // Create full profile object
        // Note: ID will be handled by App.tsx using current user
        const profile: UserProfile = {
            id: '', // filled by App
            name,
            email,
            avatar: `https://api.dicebear.com/7.x/initials/svg?seed=${name}`
        };
        onComplete(profile, selectedTemplate || undefined);
    };

    const handleGuestAccess = async () => {
        setIsLoading(true);
        try {
            const { error: signInError } = await supabase.auth.signInAnonymously();
            if (signInError) throw signInError;
            setIsNewUser(true);
            setStep('profile');
        } catch (err: unknown) {
            console.error('Guest access error:', err);
            const errorObj = err as { message?: string; status?: number };
            let message = errorObj.message || 'An error occurred';

            // Check for Captcha specific error (usually 500 or specific message)
            if (
                message.includes('captcha') ||
                (errorObj.status === 500 && message.includes('verification process failed'))
            ) {
                message = 'Captcha is required but not configured. Please disable "Enable Captcha protection" in your Supabase Project Settings > Authentication > Security.';
            }

            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    // --- Render Logic ---

    return (
        <div className="fixed inset-0 z-[9999] bg-slate-50 dark:bg-[#0E0E14] flex flex-col items-center justify-center font-sans overflow-hidden transition-colors duration-500">
            {/* Background Decorations */}
            <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />

            <div className="relative w-full max-w-md p-8 animate-in fade-in zoom-in-95 duration-500">

                {/* Logo & Header */}
                <div className="text-center mb-10 space-y-4">
                    <img
                        src="/notescape.png"
                        alt="Logo"
                        className="w-20 h-20 mx-auto rounded-2xl shadow-xl shadow-indigo-500/20 mb-6 object-cover"
                    />
                    {step === 'email' && (
                        <div className="space-y-2 animate-in slide-in-from-bottom-4 fade-in duration-500">
                            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Welcome to NotesScape</h1>
                            <p className="text-slate-500 dark:text-slate-400">Your infinite canvas for ideas.</p>
                            {onBack && (
                                <button
                                    onClick={onBack}
                                    className="text-sm text-indigo-500 hover:text-indigo-600 font-medium flex items-center justify-center gap-1 mx-auto mt-2 transition-colors"
                                >
                                    <Icons.ArrowLeft className="w-3 h-3" /> Back to Home
                                </button>
                            )}
                        </div>
                    )}
                    {step === 'otp' && (
                        <div className="space-y-2 animate-in slide-in-from-right-4 fade-in duration-300">
                            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Check your email</h1>
                            <p className="text-slate-500 dark:text-slate-400 text-sm">We sent a code to <span className="text-indigo-500 font-medium">{email}</span></p>
                        </div>
                    )}
                    {step === 'profile' && (
                        <div className="space-y-2 animate-in slide-in-from-right-4 fade-in duration-300">
                            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">What should we call you?</h1>
                            <p className="text-slate-500 dark:text-slate-400 text-sm">Let's verify your profile details.</p>
                        </div>
                    )}
                    {step === 'setup' && (
                        <div className="space-y-2 animate-in slide-in-from-right-4 fade-in duration-300">
                            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Quick Setup</h1>
                            <p className="text-slate-500 dark:text-slate-400 text-sm">Customize your creative space.</p>
                        </div>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20 text-red-600 dark:text-red-400 text-sm font-medium text-center animate-in shake">
                        {error}
                    </div>
                )}

                {/* Step Content */}
                <div className="bg-white dark:bg-[#1A1A22] rounded-3xl p-2 shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-100 dark:border-slate-800 backdrop-blur-sm">

                    {step === 'email' && (
                        <form onSubmit={handleSendOtp} className="p-6 space-y-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase tracking-wider text-slate-400 ml-1">Email Address</label>
                                <div className="relative group">
                                    <Icons.Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="name@example.com"
                                        className="w-full bg-slate-50 dark:bg-[#252630] border border-slate-200 dark:border-slate-700 rounded-xl py-4 pl-12 pr-4 text-slate-900 dark:text-white font-medium outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all placeholder:text-slate-400"
                                        autoFocus
                                        required
                                    />
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading || !email}
                                className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-lg shadow-lg shadow-indigo-600/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isLoading ? <Icons.RefreshCcw className="w-5 h-5 animate-spin" /> : 'Continue with Email'}
                            </button>
                            <div className="relative py-2">
                                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-100 dark:border-slate-800" /></div>
                                <div className="relative flex justify-center"><span className="bg-white dark:bg-[#1A1A22] px-4 text-xs text-slate-400 uppercase tracking-widest">Or</span></div>
                            </div>
                            <button
                                type="button"
                                onClick={handleGuestAccess}
                                className="w-full py-4 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl font-bold transition-all border border-slate-200 dark:border-slate-700"
                            >
                                Continue as Guest
                            </button>
                        </form>
                    )}

                    {step === 'otp' && (
                        <form onSubmit={handleVerifyOtp} className="p-6 space-y-6">
                            <div className="space-y-4 text-center">
                                <input
                                    ref={otpInputRef}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={6}
                                    value={otpCode}
                                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    placeholder="000 000"
                                    className="w-full bg-slate-50 dark:bg-[#252630] border-2 border-slate-200 dark:border-slate-700 rounded-2xl py-6 text-center text-3xl font-mono font-bold text-slate-900 dark:text-white tracking-[0.5em] outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all placeholder:text-slate-300 dark:placeholder:text-slate-700"
                                />
                                <div className="flex justify-between text-sm">
                                    <button type="button" onClick={() => setStep('email')} className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">Change email</button>
                                    <button type="button" onClick={() => handleSendOtp()} className="text-indigo-600 dark:text-indigo-400 font-medium hover:underline">Resend code</button>
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading || otpCode.length !== 6}
                                className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-lg shadow-lg shadow-indigo-600/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isLoading ? <Icons.RefreshCcw className="w-5 h-5 animate-spin" /> : 'Verify Code'}
                            </button>
                        </form>
                    )}

                    {step === 'profile' && (
                        <form onSubmit={handleCreateProfile} className="p-6 space-y-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase tracking-wider text-slate-400 ml-1">Display Name</label>
                                <div className="relative group">
                                    <Icons.User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                                    <input
                                        ref={nameInputRef}
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Enter your name"
                                        className="w-full bg-slate-50 dark:bg-[#252630] border border-slate-200 dark:border-slate-700 rounded-xl py-4 pl-12 pr-4 text-slate-900 dark:text-white font-medium outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all placeholder:text-slate-400"
                                        required
                                    />
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={!name.trim()}
                                className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-lg shadow-lg shadow-indigo-600/20 transition-all disabled:opacity-50"
                            >
                                Continue
                            </button>
                        </form>
                    )}

                    {step === 'setup' && (
                        <div className="p-6 space-y-8">
                            <div className="space-y-3">
                                <label className="text-xs font-bold uppercase tracking-wider text-slate-400 ml-1">Theme</label>
                                <div className="grid grid-cols-3 gap-3">
                                    {[
                                        { id: 'light', label: 'Light', icon: Icons.Sun },
                                        { id: 'dark', label: 'Dark', icon: Icons.Moon },
                                        { id: 'system', label: 'Auto', icon: Icons.Monitor }
                                    ].map((t) => (
                                        <button
                                            key={t.id}
                                            onClick={() => onThemeChange(t.id as AppTheme)}
                                            className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all ${currentTheme === t.id
                                                ? 'bg-indigo-50 dark:bg-indigo-500/20 border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                                : 'bg-slate-50 dark:bg-slate-800 border-transparent text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                                                }`}
                                        >
                                            <t.icon className="w-5 h-5" />
                                            <span className="text-xs font-bold">{t.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label className="text-xs font-bold uppercase tracking-wider text-slate-400 ml-1">Start with</label>
                                <select
                                    className="w-full bg-slate-50 dark:bg-[#252630] border border-slate-200 dark:border-slate-700 rounded-xl py-4 px-4 text-slate-900 dark:text-white font-medium outline-none focus:border-indigo-500 transition-all appearance-none cursor-pointer"
                                    onChange={(e) => {
                                        const template = BOARD_TEMPLATES.find(t => t.id === e.target.value);
                                        setSelectedTemplate(template || null);
                                    }}
                                    defaultValue=""
                                >
                                    <option value="">Blank Canvas</option>
                                    {BOARD_TEMPLATES.map(t => (
                                        <option key={t.id} value={t.id}>{t.title} - {t.description}</option>
                                    ))}
                                </select>
                            </div>

                            <button
                                onClick={handleComplete}
                                className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-lg shadow-lg shadow-indigo-600/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
                            >
                                Get Started
                            </button>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default Onboarding;
